import { showToast } from "@/lib/utils/toast";
import { dataEntryService } from "../services/data-entry.services";
import { disclosureStore } from "../stores/disclosure.store";
import { entryDataStore } from "../stores/entry-data.store";
import { entryUIStore } from "../stores/entry-ui.store";
import { promiseWithMinDelay } from "@/lib/utils/promiseWithMinDelay";
import { organizationService } from "@/features/organization";
import {
  disambiguateOrgDisplayNames,
  flattenOrgTree,
} from "@/features/organization/utils/organization.helpers";
import { authSessionStore } from "@/features/auth/stores/auth-session.store";
import {
  isSuperAdminRole,
  normalizeOrgId,
} from "@/features/esg/utils/org-scope.utils";
import { stripGroupNodes, filterOrgTreeByIds } from "../utils/orgTree.helpers";
import { buildAssignedFrameworks } from "@/lib/utils/frameworks.utils";
import { loadOrgHierarchyWithStatus } from "@/components/common/organization.utils";

export const loadDisclosures = async ({ initFrameworks = false } = {}) => {
  const { setDisclosures, setReportingBoundary, setFrameworks } =
    disclosureStore;
  const { setLoadingDisclosures } = entryUIStore;

  setLoadingDisclosures(true);

  try {
    // Load all assigned disclosures once. Sidebar search is applied
    // client-side (see disclosure.store.filteredDisclosures) so dependency
    // navigation can resolve any code without the local list being a
    // search-narrowed subset.
    const data = await dataEntryService.getMasterDataDisclosures();

    const disclosures = data?.disclosures ?? [];
    setDisclosures(disclosures);

    const boundary = data?.reportingBoundary ?? null;
    setReportingBoundary(boundary);

    if (initFrameworks) {
      setFrameworks(
        buildAssignedFrameworks({
          disclosures,
          frameworks: boundary?.frameworks,
        }),
      );
    }
  } catch {
    showToast.error("Failed to load disclosures");
  } finally {
    setLoadingDisclosures(false);
  }
};

export const loadDisclosurePresence = async () => {
  try {
    const data = await dataEntryService.getDisclosurePresence();
    disclosureStore.setDisclosurePresence(data?.disclosureIds ?? []);
  } catch {
    disclosureStore.setDisclosurePresence([]);
  }
};

// Epoch counter for loadOrgList: only the latest invocation may write the
// trees and the loaded flag, so an older overlapping request fulfilling
// late can never overwrite fresher data or re-enable pruning against a
// stale tree.
let orgListLoadEpoch = 0;

export const loadOrgList = async () => {
  const {
    setOrgList,
    setOrgTree,
    setFilteredOrgTree,
    setOrgTreeLoaded,
    setOrgLookupMap,
  } = entryDataStore;

  // A reload starts a fresh unknown-scope window: consumers gating on the
  // loaded flag must wait for THIS fetch, not act on the previous visit's
  // tree while the new one is in flight.
  orgListLoadEpoch += 1;
  const epoch = orgListLoadEpoch;
  setOrgTreeLoaded(false);

  const [hierarchyResult, namesResult] = await Promise.allSettled([
    promiseWithMinDelay(loadOrgHierarchyWithStatus(organizationService)),
    organizationService.fetchNames(),
  ]);

  if (epoch !== orgListLoadEpoch) return;

  if (hierarchyResult.status === "fulfilled") {
    const orgTree = hierarchyResult.value;
    if (orgTree) {
      setOrgTree(orgTree);
      const flattenOrgs = orgTree.length > 0 ? flattenOrgTree(orgTree) : [];
      setOrgList(disambiguateOrgDisplayNames(flattenOrgs));

      const user = authSessionStore.user;
      if (isSuperAdminRole(user?.role)) {
        setFilteredOrgTree(orgTree.length > 0 ? stripGroupNodes(orgTree) : []);
      } else {
        const userOrgIds = user?.organizationUnitId || [];
        const allowedSet = new Set(
          userOrgIds.map((id) => normalizeOrgId(id)).filter(Boolean),
        );
        if (user?.assignedToParentCompany && user?.companyId) {
          allowedSet.add(normalizeOrgId(user.companyId));
        }
        setFilteredOrgTree(filterOrgTreeByIds(orgTree, allowedSet));
      }
      // Only a successful hierarchy fetch marks the org scope as loaded;
      // on failure the scope stays unknown and consumers must not treat
      // the empty trees as a real zero-scope.
      setOrgTreeLoaded(true);
    }
  } else {
    console.log("err", hierarchyResult.reason);
    showToast.error("Failed to load organization list");
  }

  if (namesResult.status === "fulfilled") {
    setOrgLookupMap(namesResult.value?.orgs || []);
  }
};

export const loadDataEntries = async () => {
  const { activeDisclosure } = disclosureStore;
  const { setStandardEntries, setSubEntries, setComputedEntries, setTargets } =
    entryDataStore;
  const { setLoadingEntries } = entryUIStore;

  setLoadingEntries(true);

  try {
    const { _id, inputType, isCalculated } = activeDisclosure || {};
    const isComputed = isCalculated && inputType !== "subEntries";

    const action =
      dataEntryService[
        isComputed
          ? "getDataEntriesByDisclosureComputed"
          : "getDataEntriesByDisclosure"
      ];

    const [data, targetsData] = await Promise.all([
      action(_id),
      dataEntryService.getTargetsByDisclosure(_id).catch(() => null),
    ]);

    setTargets(targetsData?.items ?? []);

    if (data) {
      if (isComputed) {
        setComputedEntries(data);
        return;
      }

      if (data.scenario === "singleEntry") {
        setStandardEntries(data);
      }

      if (data.scenario === "subEntries") {
        setSubEntries(data);
      }
    }

    entryUIStore.bumpEntriesVersion();
  } catch {
    showToast.error("Failed to load entries");
  } finally {
    setLoadingEntries(false);
  }
};
